import React from 'react';
import { Game } from '../types/games_api_types';
import { Sport } from '@/types';
import classes from './GameScorecard.module.css';
import { TEAMS } from '../teams';
import { useContentVisibility } from '../hooks/useContentVisibility';
import { FootballField } from './FootballField';
import { scheduleItemTimeString } from '../utils';
import TextCircle from '../shared/TextCircle';
import BattedBallBases from './BattedBallBases';

function transformSport(sport: Sport) {
  return sport
    .split('_')
    .map((word) => {
      if (word === 'mens') word = "Men's";
      if (word === 'womens') word = "Women's";
      return word[0].toUpperCase() + word.substring(1);
    })
    .join(' ');
}

function renderedPeriodCount(game: Game): number {
  return Math.max(game.base_period_count, game.away_score_array.length);
}

function totalWord(game: Game): string {
  if (game.sport === 'volleyball') {
    return 'Sets';
  }
  return 'Total';
}

function rangeArray(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i + 1);
}

function lastNonNullValue(values: Array<number | null>) {
  for (let i = values.length - 1; i >= 0; i--) {
    if (values[i] !== null) return values[i];
  }
  return null;
}

export default function GameScorecard({
  game,
  today,
}: {
  game: Game;
  today?: boolean;
}) {
  const teamNameClass = today ? classes.todayTeamName : classes.teamName;
  const teamNameAbbrevClass = today
    ? classes.todayTeamNameAbbrev
    : classes.teamNameAbbrev;
  const totalWordClass = today ? classes.todayTotalWord : classes.totalWord;
  const { showTeamLogos, showStudentAthletes } = useContentVisibility();
  const period_range = rangeArray(game.base_period_count);
  const awayLatestScore = lastNonNullValue(game.away_score_array);
  const homeLatestScore = lastNonNullValue(game.home_score_array);

  return (
    <div
      className={[
        classes.scorecardGame,
        showStudentAthletes ? '' : classes.noAthlete,
      ].join(' ')}
    >
      {showStudentAthletes && (
        <img
          className={classes.slantedHero}
          src={`/images/today/${game.season}-${game.sport}.png`}
        />
      )}

      <div className={classes.header}>
        <div>
          {transformSport(game.sport)} {game.vs_string}
        </div>

        <div className={classes.right}>
          {game.tv_channel !== null ? `${game.tv_channel} - ` : ''}
          {game.state === 'in_progress' || game.state === 'finished'
            ? game.status_text
            : scheduleItemTimeString(game)}
        </div>
      </div>

      <div className={classes.liveTrack} style={{ justifyContent: 'center' }}>
        {game.state === 'in_progress' && game.sport === 'football' && (
          <div className={[classes.footballField, classes.smallOnly].join(' ')}>
            <FootballField game={game} />
            <br />
            <div className={classes.statusText}>{game.status_text}</div>
          </div>
        )}
      </div>
      <div className={classes.liveTrack}>
        <div className={classes.mainRow}>
          {game.state !== 'upcoming' && (
            <div className={classes.mainScore}>
              {showTeamLogos ? (
                <img
                  src={`/images/team_logos/${TEAMS[game.away_team].logo}`}
                ></img>
              ) : (
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.away_team} />
                </div>
              )}
              <span>{game.away_total_score}</span>
            </div>
          )}
          {game.state === 'in_progress' && game.sport === 'football' && (
            <div className={classes.footballField}>
              <FootballField game={game} />
            </div>
          )}
          {(game.state === 'finished' ||
            (game.state === 'in_progress' && game.sport !== 'football')) && (
            <div style={{ textAlign: 'center' }}>
              {game.sport === 'volleyball' && game.state === 'in_progress' && (
                <div className={classes.statusText}>
                  {awayLatestScore}-{homeLatestScore}
                </div>
              )}
              {(game.sport === 'baseball' || game.sport === 'softball') &&
                game.state === 'in_progress' &&
                game.current_runners !== null &&
                game.current_runners !== undefined && (
                  <div className={classes.battedBallBases}>
                    <BattedBallBases current_runners={game.current_runners} />
                  </div>
                )}
              <div className={classes.statusText}>{game.status_text}</div>
              {(game.sport === 'baseball' || game.sport === 'softball') &&
                game.state === 'in_progress' &&
                game.current_outs !== null &&
                game.current_outs !== undefined && (
                  <div className={classes.statusText}>
                    {game.current_outs}{' '}
                    {game.current_outs === 1 ? 'OUT' : 'OUTS'}
                  </div>
                )}
            </div>
          )}
          {game.state !== 'upcoming' && (
            <div className={classes.mainScore}>
              <span>{game.home_total_score}</span>
              {showTeamLogos ? (
                <img
                  src={`/images/team_logos/${TEAMS[game.home_team].logo}`}
                ></img>
              ) : (
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.home_team} />
                </div>
              )}
            </div>
          )}
        </div>
        {game.state === 'in_progress' && game.sport === 'football' && (
          <div className={classes.statusText}>{game.status_text}</div>
        )}
      </div>
      {game.sport === 'football' &&
        game.state === 'in_progress' &&
        game.away_team_timeouts !== null &&
        game.home_team_timeouts !== null && (
          <div className={classes.liveTrack}>
            <div className={classes.mainRow}>
              <div className={classes.timeouts}>
                <span>TOs:</span>
                <span className={classes.timeoutPips}>
                  {Array.from({ length: 3 }, (_, i) => {
                    const isFilled = (game.away_team_timeouts ?? 0) >= i + 1;
                    return (
                      <i
                        key={`away-timeout-${i}`}
                        className={
                          isFilled
                            ? 'fa-solid fa-circle'
                            : 'fa-regular fa-circle'
                        }
                      />
                    );
                  })}
                </span>
              </div>
              <div
                className={classes.timeouts}
                style={{ justifyContent: 'right' }}
              >
                <span>TOs:</span>
                <span className={classes.timeoutPips}>
                  {Array.from({ length: 3 }, (_, i) => {
                    const isFilled = (game.home_team_timeouts ?? 0) >= i + 1;
                    return (
                      <i
                        key={`away-timeout-${i}`}
                        className={
                          isFilled
                            ? 'fa-solid fa-circle'
                            : 'fa-regular fa-circle'
                        }
                      />
                    );
                  })}
                </span>
              </div>
            </div>
          </div>
        )}

      {(game.sport === 'mens_basketball' ||
        game.sport === 'womens_basketball') &&
        game.state == 'in_progress' &&
        game.away_timeouts !== null &&
        game.home_timeouts !== null &&
        game.away_fouls !== null &&
        game.home_fouls !== null &&
        game.possession_arrow !== null && (
          <div className={classes.liveTrack}>
            <div className={classes.mainRow}>
              <div className={classes.timeouts}>
                <span>TOs: {game.away_timeouts}</span>
                <span>F: {game.away_fouls}</span>
              </div>
              <div
                className={classes.timeouts}
                style={{ justifyContent: 'center' }}
              >
                {game.possession_arrow === 'away' && (
                  <i className={'fa-solid fa-caret-left'} />
                )}
                <span>POSS</span>
                {game.possession_arrow === 'home' && (
                  <i className={'fa-solid fa-caret-right'} />
                )}
              </div>
              <div
                className={classes.timeouts}
                style={{ justifyContent: 'right' }}
              >
                <span>F: {game.home_fouls}</span>
                <span>TOs: {game.home_timeouts}</span>
              </div>
            </div>
          </div>
        )}

      <div className={classes.scorecard}>
        {game.state === 'upcoming' ? (
          <div className={classes.logoVersus}>
            {showTeamLogos ? (
              <>
                <img
                  src={`/images/team_logos/${TEAMS[game.away_team].logo}`}
                ></img>
                {game.neutral_site ? 'vs.' : 'at'}
                <img
                  src={`/images/team_logos/${TEAMS[game.home_team].logo}`}
                ></img>
              </>
            ) : (
              <>
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.away_team} />
                </div>
                {game.neutral_site ? 'vs.' : 'at'}
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.home_team} />
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            className={[
              classes.scoreGrid,
              `periods-${renderedPeriodCount(game)}`,
            ].join(' ')}
          >
            <div className={classes.header}></div>
            {period_range.map((period, idx) => {
              return (
                <div key={`period-${idx}`} className={classes.header}>
                  {period}
                </div>
              );
            })}
            {renderedPeriodCount(game) > period_range.length && (
              <>
                {[
                  ...Array(renderedPeriodCount(game) - period_range.length),
                ].map((_, i) => {
                  if (game.sport === 'baseball' || game.sport === 'softball') {
                    return (
                      <div key={`extra-inning-${i}`} className={classes.header}>
                        {i + 1 + period_range.length}
                      </div>
                    );
                  }
                  return (
                    <div key={`extra-period-${i}`} className={classes.header}>
                      OT {i + 1}
                    </div>
                  );
                })}
              </>
            )}
            <div className={classes.totalScore}>
              <span className={totalWordClass}>{totalWord(game)}</span>
            </div>
            <div className={classes.teamNameContainer}>
              {showTeamLogos ? (
                <>
                  <img
                    src={`/images/team_logos/${TEAMS[game.away_team].logo}`}
                  />
                </>
              ) : (
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.away_team} />
                </div>
              )}
              <span className={teamNameClass}>{game.away_team}</span>
              <span className={teamNameAbbrevClass}>
                {TEAMS[game.away_team].endzone_abbreviation}
              </span>
            </div>
            {game.away_score_array.map((score, idx) => {
              if (score === null && game.state === 'finished') {
                return <div key={`away-${idx}`}>X</div>;
              }

              return <div key={`away-${idx}`}>{score}</div>;
            })}
            <div className={classes.totalScore}>{game.away_total_score}</div>
            <div className={classes.teamNameContainer}>
              {showTeamLogos ? (
                <>
                  <img
                    src={`/images/team_logos/${TEAMS[game.home_team].logo}`}
                  />
                </>
              ) : (
                <div className={classes.textCircle}>
                  <TextCircle teamName={game.home_team} />
                </div>
              )}
              <span className={teamNameClass}>{game.home_team}</span>
              <span className={teamNameAbbrevClass}>
                {TEAMS[game.home_team].endzone_abbreviation}
              </span>
            </div>
            {game.home_score_array.map((score, idx) => {
              if (score === null && game.state === 'finished') {
                return <div key={`home-${idx}`}>X</div>;
              }

              return <div key={`home-${idx}`}>{score}</div>;
            })}
            <div className={classes.totalScore}>{game.home_total_score}</div>
          </div>
        )}
      </div>
    </div>
  );
}
